import React, { useMemo, useState } from "react";
import { Input } from "antd";
import { GET_EMAIL_CHANNELS_CURSOR } from "graphql/query/email-channel-gql";
import Organizer from "components/organizer";
import EmailChannel from "components/email-channel";
import Icons from "components/icons";
import { Tables } from "components/layout";
import { DrawerStandart, Localize } from "components/service";
import { localizeByKey } from "components/service/localize";
import { useBreadCrumbs } from "components/use-hooks";
import type { TOrganizer } from "components/organizer/helpers/organizer-types-helper";
import type {
  TEmailChannel,
  TEmailChannelTableRow,
} from "components/email-channel/helpers/email-channel-types-helper";

const { Search } = Input;

interface IOrganizerEmailChannelsSubPageProps {
  organizer: TOrganizer | undefined;
}

const OrganizerEmailChannelsSubPage: React.FC<
  IOrganizerEmailChannelsSubPageProps
> = ({ organizer }) => {
  const [searchText, setSearchText] = useState("");
  const [variables, setVariables] = useState<Record<string, any>>({});
  const [channels, setChannels] = useState<TEmailChannel[] | undefined>();
  const [guideOpen, setGuideOpen] = useState(false);
  const organizerBasePath = Organizer.Const.basePath;
  const routeUrl = `${organizerBasePath}/${organizer?.id}/${EmailChannel.Const.routePath}`;
  const exhibitionTypeIDs =
    organizer?.exhibitionTypes?.map(({ id }) => id) ?? [];

  useBreadCrumbs([
    {
      label: localizeByKey("BREADCRUMBS.Label_Organizers"),
      path: organizerBasePath,
    },
    {
      label: organizer?.title ?? localizeByKey("VARIABLE.Label_EMPTY"),
      path: `${organizerBasePath}/${organizer?.id}`,
    },
    {
      label: localizeByKey("EMAIL_CHANNEL.Page_Title"),
      path: routeUrl,
    },
  ]);

  const tableHelper = useMemo(
    () => EmailChannel.Helpers.TableMain(() => setGuideOpen(true)),
    []
  );
  const isEmpty = Boolean(organizer?.id) &&
    !searchText.trim() &&
    !variables.text &&
    (exhibitionTypeIDs.length === 0 || channels?.length === 0);

  return (
    <>
      <div
        className={`page-organizer-email-channels row-grid col-lg-2 ${isEmpty ? "is-empty" : ""}`.trim()}
      >
        <div className="col-left">
          <Tables.Cursor
            model={EmailChannel.Const.model}
            query={GET_EMAIL_CHANNELS_CURSOR}
            querySkip={!organizer?.id || exhibitionTypeIDs.length === 0}
            autoLoadAll
            varSet={{ perPage: 50 }}
            routeUrl={routeUrl}
            searchText={searchText}
            currentObj={organizer}
            objectWhere={{
              column: "EXHIBITION_TYPE_ID",
              operator: "IN",
              value: exhibitionTypeIDs,
            }}
            objOrderBy={[
              { column: "EXHIBITION_TYPE_ID", order: "ASC" },
              { column: "ID", order: "DESC" },
            ]}
            tableHelper={tableHelper}
            setVariables={setVariables}
            setDataModel={(data) => setChannels(data)}
            emptyView={
              organizer ? (
                <EmailChannel.EmptyState
                  action={
                    <EmailChannel.Forms.ConnectModal
                      organizer={organizer}
                      variables={variables}
                      buttonLabel={
                        <Localize>EMAIL_CHANNEL.Button_ConnectChannel</Localize>
                      }
                      buttonClassName="email-channel-empty-connect-button"
                    />
                  }
                />
              ) : undefined
            }
            extraClass="table-email-channels"
            rowClassName={(record: TEmailChannelTableRow) =>
              `email-channel-${record.rowType}-row`
            }
            expandable={{
              defaultExpandAllRows: true,
              expandRowByClick: true,
              indentSize: 0,
              expandIcon: ({ expanded, onExpand, record }) =>
                record.rowType === "group" ? (
                  <button
                    type="button"
                    className={`email-channel-expand ${expanded ? "is-expanded" : ""}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onExpand(record, event);
                    }}
                  >
                    <Icons.Arrow />
                  </button>
                ) : (
                  <span className="email-channel-expand-placeholder" />
                ),
            }}
          >
            <div className="table-action-bar">
              <div className="col">
                <Search
                  className="filter-search"
                  prefix={<Icons.Search />}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </div>
              <div className="col">
                {organizer && (
                  <EmailChannel.Forms.ConnectModal
                    organizer={organizer}
                    variables={variables}
                    buttonLabel={
                      <Localize>EMAIL_CHANNEL.Button_ConnectNew</Localize>
                    }
                  />
                )}
              </div>
            </div>
          </Tables.Cursor>
        </div>
        <div className="col-right" />
      </div>

      <DrawerStandart
        showModal={guideOpen}
        setShowModal={setGuideOpen}
        width={500}
        extraClass="email-channel-guide-drawer"
      >
        <EmailChannel.Guide />
      </DrawerStandart>
    </>
  );
};

export default OrganizerEmailChannelsSubPage;
